import type { QueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/hooks/query-keys'

/**
 * Refresh everything that caches a project's status configuration after any
 * status change (project defaults, per-type sources/custom sets, or an org
 * default edit that remapped tasks). Used by ProjectStatusSettings and the
 * task-type status hooks so their side effects stay in sync.
 */
export async function invalidateProjectStatusCaches(
  queryClient: QueryClient,
  projectId: string,
) {
  // Notify mounted task views FIRST so they can prepare for the refetch.
  window.dispatchEvent(
    new CustomEvent('project-statuses-updated', { detail: { projectId } }),
  )

  // useTaskData uses keys like ['tasks', moduleType, moduleId, ...], so
  // invalidate from the base ['tasks'] key and force refetch of inactive
  // queries too.
  await queryClient.invalidateQueries({
    queryKey: ['tasks'],
    refetchType: 'all',
  })
  // Task groups also cache status information.
  await queryClient.invalidateQueries({
    queryKey: ['task-groups'],
    refetchType: 'all',
  })
  // TaskStatusField lazily resolves the exact project + task-type workflow
  // when its popover opens. Drop any previous resolver response as well.
  await queryClient.invalidateQueries({
    queryKey: ['task-status', 'resolved', projectId],
    refetchType: 'all',
  })
  // Statuses are part of project settings.
  await queryClient.invalidateQueries({
    queryKey: queryKeys.projects.detail(projectId),
    refetchType: 'all',
  })
  // useTaskStatusResolver resolves from the projects list cache.
  await queryClient.invalidateQueries({
    queryKey: queryKeys.projects.lists(),
    refetchType: 'all',
  })
  // Per-type status configs and source badges under this project.
  await queryClient.invalidateQueries({
    queryKey: queryKeys.taskTypes.byProject(projectId),
  })
}
